import { ActorRefFrom, assign, createMachine, send, spawn } from 'xstate';
import { forwardTo } from 'xstate/lib/actions';

import { AddressFormFeatures, Recipient, ValidateAddressResponse } from 'api/api';

import { AddressChanged, AutocompleteChange } from './shared-types';
import { StreetNumberMachine, createStreetNumberMachine } from './street-number-machine';
import { SuggestionMachine, createSuggestionMachine } from './suggestion-machine';
import { suggestionService } from './suggestion-service';

export const createAddressMachine = ({
  country,
  initialAddress,
  addressVerified,
  provider,
  extendedValidation,
}: {
  country: string;
  initialAddress: Recipient | undefined;
  addressVerified: boolean;
  provider: AddressFormFeatures['suggestion_provider'];
  extendedValidation: boolean;
}) => {
  return createMachine(
    {
      predictableActionArguments: true,
      context: {
        country: country,
        streetNumber: {} as Context['streetNumber'],
        // Validation response from endpoint
        validation: null,
        // Suggestion actor
        suggestion: {} as Context['suggestion'],
        provider,
      },
      tsTypes: {} as import('./address-machine.typegen').Typegen0,
      schema: { context: {} as Context, events: {} as Events, services: {} as Services },
      entry: assign({
        streetNumber: () =>
          spawn(
            createStreetNumberMachine(
              country,
              extendedValidation,
              addressVerified ? initialAddress : undefined
            ),
            {
              name: 'street_number',
            }
          ),
        suggestion: () =>
          spawn(
            createSuggestionMachine(
              country,
              extendedValidation,
              addressVerified ? initialAddress : undefined
            ),
            {
              name: 'suggestion',
            }
          ),
      }),
      id: 'address-machine',
      initial: 'waiting',
      states: {
        waiting: {
          on: {
            SUBMIT: [
              {
                cond: 'no_suggestion_provider',
                target: 'submitted',
              },
              {
                cond: 'street_number&suggestion_ready',
                target: 'submitted',
              },
              {
                cond: 'street_number_ready&suggestion_needs_confirmation',
                target: 'submitted',
              },
              {
                target: 'validating',
              },
            ],
          },
        },
        validating: {
          invoke: {
            src: 'suggestionService',
            id: 'validate',
            onDone: [
              {
                actions: [
                  'updateValidationContext',
                  'sendSuggestionToStreet',
                  'sendSuggestionToSuggestionActor',
                ],
              },
            ],
          },
          on: {
            STREET_READY: {
              actions: 'updateFormValuesWithSuggestionStreetDetails',
            },
            SUGGESTION_READY: [
              {
                cond: 'street_is_ready',
                target: 'submitted',
              },
              {
                target: 'waiting',
              },
            ],
            SKIP: {
              target: 'waiting',
            },
          },
        },
        submitted: {
          entry: 'submitForm',
          on: {
            ADDRESS_UPDATE_FAIL: {
              target: 'waiting',
            },
          },
        },
      },
      on: {
        RESET_STATE: {
          // TODO check if this doesn't break actors
          target: 'waiting',
        },
        ADDRESS_CHANGED: {
          actions: ['forwardToStreetNumberActor', 'forwardToSuggestionActor'],
        },
        AUTOCOMPLETE_CHANGE: {
          actions: ['forwardToStreetNumberActor', 'forwardToSuggestionActor'],
        },
      },
    },
    {
      services: {
        suggestionService: (context, event) =>
          suggestionService(event.payload, context.country, extendedValidation, context.provider),
      },
      actions: {
        updateValidationContext: assign({
          validation: (_, event) => event.data,
        }),
        forwardToStreetNumberActor: forwardTo(ctx => ctx.streetNumber),
        forwardToSuggestionActor: forwardTo(ctx => ctx.suggestion),
        sendSuggestionToSuggestionActor: send(
          (_, event) => ({ type: 'SUGGESTION_UPDATE', payload: event.data }),
          {
            to: 'suggestion',
          }
        ),
        sendSuggestionToStreet: send(
          (_, event) => ({ type: 'SUGGESTION_UPDATE', payload: event.data }),
          {
            to: 'street_number',
          }
        ),
      },
      guards: {
        no_suggestion_provider: context => context.provider === 'no_provider',
        'street_number_ready&suggestion_needs_confirmation': context =>
          !!context.streetNumber?.getSnapshot()?.matches('ready') &&
          !!context.suggestion?.getSnapshot()?.matches('needs_confirmation'),
        street_is_ready: context => !!context.streetNumber?.getSnapshot()?.matches('ready'),
        'street_number&suggestion_ready': context =>
          !!context.streetNumber?.getSnapshot()?.matches('ready') &&
          !!context.suggestion?.getSnapshot()?.matches('ready'),
      },
    }
  );
};

interface Context {
  streetNumber: ActorRefFrom<StreetNumberMachine>;
  suggestion: ActorRefFrom<SuggestionMachine>;
  validation: ValidateAddressResponse | null;
  country: string;
  provider: AddressFormFeatures['suggestion_provider'];
}

type Events =
  | AutocompleteChange
  | AddressChanged
  | { type: 'SUGGESTION_READY' }
  | { type: 'SKIP' }
  | { type: 'STREET_READY'; payload: { street: string; street_number: string } }
  | { type: 'ADDRESS_UPDATE_FAIL' }
  | { type: 'SUBMIT'; payload: Recipient }
  | { type: 'RESET_STATE' };

type Services = {
  suggestionService: {
    data: ValidateAddressResponse;
  };
};

export type RecipientAddressMachine = ReturnType<typeof createAddressMachine>;
